import { useState } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import BackgroundPattern from "@/components/BackgroundPattern";

const testimonials = [
  {
    name: "Maria Gutierrez",
    location: "Albuquerque, NM",
    text: "I couldn't be happier with the new roof installed by Zenith. The team was professional, efficient, and meticulous. Our home feels brand new!",
    service: "Roofing",
  },
  {
    name: "David Chen",
    location: "Rio Rancho, NM",
    text: "Zenith made renovating my home seamless! Their attention to detail exceeded expectations. From start to finish, I felt informed and confident.",
    service: "General Construction",
  },
  {
    name: "Sarah & Mark Thompson",
    location: "Corrales, NM",
    text: "We have saved so much thanks to Zenith! Our energy bills have significantly decreased. The solar installation was quick and the team explained everything clearly.",
    service: "Solar",
  },
  {
    name: "James Rivera",
    location: "Los Lunas, NM",
    text: "Outstanding craftsmanship and customer service. Zenith replaced our aging tile roof and it looks incredible. They even helped us navigate our insurance claim.",
    service: "Roofing",
  },
  {
    name: "Angela Montoya",
    location: "Bernalillo, NM",
    text: "From the free consultation to the final walkthrough, Zenith was transparent about everything. No surprises, just quality work delivered on time and on budget.",
    service: "General Construction",
  },
];

const TestimonialsSection = () => {
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx((i) => (i - 1 + testimonials.length) % testimonials.length);
  const next = () => setIdx((i) => (i + 1) % testimonials.length);
  const t = testimonials[idx];

  return (
    <section id="testimonials" className="section-padding bg-section-mid relative">
      <BackgroundPattern variant="roofline" />
      <div className="container mx-auto max-w-4xl text-center relative z-10">
        <p className="text-sm font-bold uppercase tracking-widest text-primary mb-3">Testimonials</p>
        <h2 className="text-3xl md:text-4xl font-black text-foreground mb-12">What Our Clients Say</h2>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3 }}
              className="bg-card border rounded-2xl p-8 md:p-12 shadow-lg"
            >
              <Quote className="h-10 w-10 text-primary mx-auto mb-6 opacity-50" />
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                ))}
              </div>
              <blockquote className="text-lg md:text-xl text-foreground leading-relaxed mb-6 max-w-2xl mx-auto">
                "{t.text}"
              </blockquote>
              <div>
                <p className="font-bold text-foreground">{t.name}</p>
                <p className="text-sm text-muted-foreground">{t.location} · {t.service}</p>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center gap-3 mt-8">
            <button onClick={prev} className="p-2.5 rounded-full border bg-card hover:bg-muted transition-colors" aria-label="Previous testimonial">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`w-6 h-6 rounded-full transition-colors flex items-center justify-center ${i === idx ? "bg-primary" : "bg-border"}`}
                  style={{ backgroundClip: 'content-box', padding: '7px' }}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <button onClick={next} className="p-2.5 rounded-full border bg-card hover:bg-muted transition-colors" aria-label="Next testimonial">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
